import { useState, useEffect, useCallback, useRef } from 'react';

import { getClientDataCollections } from 'api/dataCollection';

/** Past sessions shown per page on a list-style Historical tab. */
export const HISTORY_PAGE_SIZE = 5;

/* The server caps page_size at 100 (PaginationParams.max_page_size). Asking for
 * more is not an error — it is silently clamped — so a sheet that requested 200
 * quietly lost everything past the hundredth entry. Range mode asks for the cap. */
const MAX_PAGE_SIZE = 100;

/**
 * Previously-collected entries for one child on one data sheet.
 *
 * Most sheets used to keep their Historical tab in local state alone: it was
 * seeded empty, written to only as the therapist entered today's data, and reset
 * whenever the target list changed. Nothing ever read the saved entries back, so
 * the tab was always blank on open and "previous sessions" meant "what you typed
 * before the last refresh". This is the single fetch they now share.
 *
 * TWO FETCH MODES, because the tabs are not all the same shape:
 *
 *  - PAGED (default) for a list of past sessions. Loads HISTORY_PAGE_SIZE at a
 *    time so opening the tab costs one small request rather than the child's
 *    entire history.
 *
 *  - RANGE (`startDate`/`endDate`) for the date × target grids. Those render one
 *    column per day across a picked window, so a page of N arbitrary records
 *    would leave columns blank where data actually exists — showing "no data"
 *    for a day that has some is worse than loading slowly. The picked window is
 *    the bound instead, which for the default week is a handful of rows.
 *
 * Config records are filtered out. OT and SLP persist their standing goal list as
 * a record flagged `isGoalsConfig` alongside real entries; it is configuration,
 * not a session, and must never appear as one in a history view.
 *
 * @param {string} childId
 * @param {string} datasheetTypeCode - clinical.ref_datasheet_type.code, e.g. 'cold_probe'
 * @param {object} [options]
 * @param {number} [options.pageSize=HISTORY_PAGE_SIZE]
 * @param {boolean} [options.enabled=true] - skip the call entirely
 * @param {string} [options.startDate] - 'yyyy-MM-dd'; switches to range mode
 * @param {string} [options.endDate]   - 'yyyy-MM-dd'
 * @returns {{
 *   records: Array, total: number, page: number, totalPages: number,
 *   setPage: Function, loading: boolean, error: Error|null, reload: Function,
 *   isEmpty: boolean
 * }} records are newest-first by collected_at.
 */
export default function useDatasheetHistory(
  childId,
  datasheetTypeCode,
  { pageSize = HISTORY_PAGE_SIZE, enabled = true, startDate = null, endDate = null } = {}
) {
  const rangeMode = Boolean(startDate || endDate);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /* Distinguishes "not fetched yet" from "fetched, nothing there" — an empty
   * state must not flash before the first response lands. */
  const [loaded, setLoaded] = useState(false);

  // A page that no longer exists (the last entry on it was deleted) would render
  // blank forever; snap back to the first page when the child or sheet changes.
  useEffect(() => {
    setPage(1);
  }, [childId, datasheetTypeCode, startDate, endDate]);

  // Guards against an out-of-order response overwriting a newer one when the
  // page is changed quickly.
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!childId || !datasheetTypeCode || !enabled) {
      setRecords([]);
      setTotal(0);
      setLoaded(Boolean(childId && datasheetTypeCode));
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = { datasheet_type_code: datasheetTypeCode };
      if (rangeMode) {
        params.page_size = MAX_PAGE_SIZE;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      } else {
        params.page = page;
        params.page_size = Math.min(pageSize, MAX_PAGE_SIZE);
      }

      const res = await getClientDataCollections(childId, params);
      if (requestId !== requestRef.current) return; // superseded

      /* Config records are dropped AFTER the server paged, so a page can come
       * back shorter than pageSize. Only OT and SLP store them, and only one per
       * child, so this costs at most one row on one page — not worth a second
       * round trip to top up. */
      const items = (res?.items || [])
        .filter((r) => r?.raw_data?.isGoalsConfig !== true)
        .slice()
        .sort((a, b) => {
          const ta = a?.collected_at ? new Date(a.collected_at).getTime() : 0;
          const tb = b?.collected_at ? new Date(b.collected_at).getTime() : 0;
          return tb - ta;
        });

      setRecords(items);
      setTotal(res?.total ?? items.length);
      setLoaded(true);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error(`[useDatasheetHistory] ${datasheetTypeCode} load failed:`, err);
      setError(err);
      setRecords([]);
      setTotal(0);
      setLoaded(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [childId, datasheetTypeCode, pageSize, enabled, page, rangeMode, startDate, endDate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const totalPages = rangeMode ? 1 : Math.max(1, Math.ceil(total / Math.min(pageSize, MAX_PAGE_SIZE)));

  return {
    records,
    total,
    page,
    totalPages,
    setPage,
    loading,
    error,
    reload,
    // True only once a response has come back empty — never while still loading.
    isEmpty: loaded && !loading && records.length === 0
  };
}

/**
 * The day an entry belongs to, as 'yyyy-MM-dd'.
 *
 * Prefers the sheet's own sessionDate (the therapist's local day, written by the
 * form) over collected_at, which is a server UTC timestamp and can land on the
 * previous day for an evening session in IST.
 */
export const historyDateKey = (record) => {
  const rd = record?.raw_data || {};
  if (rd.sessionDate) return String(rd.sessionDate).slice(0, 10);
  if (record?.collected_at) return String(record.collected_at).slice(0, 10);
  return null;
};

/**
 * Fold newest-first records into a `{ [dateKey]: value }` map, keeping the FIRST
 * entry seen for each day — which, given the sort order, is the latest entry for
 * that day. A day re-entered after a correction shows the correction.
 *
 * @param {Array} records - newest-first, as returned by the hook
 * @param {Function} pick - (record) => value for that day, or undefined to skip
 */
export const latestPerDay = (records, pick) => {
  const out = {};
  (records || []).forEach((record) => {
    const key = historyDateKey(record);
    if (!key || out[key] !== undefined) return;
    const value = pick(record);
    if (value !== undefined) out[key] = value;
  });
  return out;
};
